/**
 * WeaveIn.ai Platform — External Referrals API (TRUS-511)
 *
 * Referrals an org makes OUTSIDE the platform: "I am offering a referral to apply to a
 * USDA Single Family Disaster Grant - the deadline is 9/30 ... I will also need to be
 * able to upload copies of all their supporting documents and a copy of the application
 * I intend to submit."
 *
 * Distinct from `services/api/referrals` — that is the clinical provider-MATCHING
 * engine (medicaid_id, health_plan, npi_number, primary_diagnosis, matched_provider_*).
 * This is a log of referrals made: who, what for, when it is due, where it got to.
 *
 * Uses the absolute production API base like the other services — never a relative
 * /api path, which Vercel proxies to the dev backend.
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';

export type ExternalReferralStatus =
  | 'open'
  | 'in_progress'
  | 'submitted'
  | 'approved'
  | 'denied'
  | 'withdrawn';

/** 'application' is the packet we filed; 'supporting' is everything gathered for it. */
export type ExternalReferralDocumentRole = 'supporting' | 'application';

export interface ExternalReferralDocument {
  doc_record_id: number;
  role: ExternalReferralDocumentRole;
  filename?: string | null;
  doc_type_name?: string | null;
  linked_at: string;
}

export interface ExternalReferral {
  id: number;
  cbo_id: number;
  member_id: number;
  agency_name: string;
  program_name?: string | null;
  purpose?: string | null;
  referral_date?: string | null;
  deadline?: string | null;
  status: ExternalReferralStatus;
  outcome_notes?: string | null;
  assigned_to?: number | null;
  assigned_to_name?: string | null;
  created_by?: number | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
  documents: ExternalReferralDocument[];
  /** Server-computed, so "due in 3 days" does not depend on the client's clock. */
  days_until_deadline?: number | null;
  is_overdue: boolean;
}

export interface ExternalReferralInput {
  agency_name: string;
  program_name?: string;
  purpose?: string;
  referral_date?: string;
  deadline?: string;
  status?: ExternalReferralStatus;
  outcome_notes?: string;
  assigned_to?: number | null;
}

class ExternalReferralsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({ baseURL, headers: { 'Content-Type': 'application/json' } });
    this.client.interceptors.request.use((cfg) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      if (cboId) cfg.headers['X-CBO-ID'] = cboId;
      return cfg;
    });
  }

  async list(memberId: number, status?: ExternalReferralStatus): Promise<ExternalReferral[]> {
    const res = await this.client.get(`/api/members/${memberId}/external-referrals`, {
      params: status ? { status } : undefined,
    });
    return res.data;
  }

  async create(memberId: number, payload: ExternalReferralInput): Promise<ExternalReferral> {
    const res = await this.client.post(`/api/members/${memberId}/external-referrals`, payload);
    return res.data;
  }

  async update(referralId: number, payload: Partial<ExternalReferralInput>): Promise<ExternalReferral> {
    const res = await this.client.put(`/api/external-referrals/${referralId}`, payload);
    return res.data;
  }

  async remove(referralId: number): Promise<void> {
    await this.client.delete(`/api/external-referrals/${referralId}`);
  }

  /**
   * Add a member document to the packet. The file is uploaded through the normal
   * Documents flow first — these are real member documents and belong on the Documents
   * tab — and then pointed at the referral.
   */
  async linkDocument(
    referralId: number,
    docRecordId: number,
    role: ExternalReferralDocumentRole = 'supporting'
  ): Promise<void> {
    await this.client.post(`/api/external-referrals/${referralId}/documents`, {
      doc_record_id: docRecordId,
      role,
    });
  }

  /** Takes it out of the packet. The document itself is untouched. */
  async unlinkDocument(referralId: number, docRecordId: number): Promise<void> {
    await this.client.delete(`/api/external-referrals/${referralId}/documents/${docRecordId}`);
  }
}

export const externalReferralsAPI = new ExternalReferralsAPI();
export default externalReferralsAPI;
